import React from "react";
import { type IconNameEnum, useTenantIcons } from "@b2bportal/core-themes";
import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import clsx from "clsx";
import defaultStyles from "./Icon.module.scss";

export interface IconComponentProp
  extends React.SVGAttributes<HTMLOrSVGElement> {
  iconName: IconNameEnum;
  className?: string;
  useCurrentColor?: boolean;
  alt?: string;
  strokeOnly?: boolean;
}

export const Icon = ({
  iconName,
  className,
  useCurrentColor = false,
  strokeOnly = false,
  alt,
  ...props
}: IconComponentProp) => {
  const styles = useUiStyles(CoreUiComponents.Icon, defaultStyles);
  const [block] = useModuleBEM(styles, CoreUiComponents.Icon, {
    currentColor: useCurrentColor,
    strokeOnly: strokeOnly,
  });

  const icons = useTenantIcons();
  const IconComponent = icons[iconName];

  // Expose a non-empty `alt` as a real accessible name (SVG ignores the
  // `alt` attribute). Treat a missing OR empty `alt` as decorative and hide
  // the icon from assistive tech (empty `alt=""` is the conventional
  // decorative marker). Explicit aria/role passed via `props` still wins.
  const a11yProps = alt
    ? { role: "img", "aria-label": alt }
    : { "aria-hidden": true, focusable: false };

  // Render the IconComponent if it exists, otherwise render null
  return IconComponent ? (
    <IconComponent
      className={clsx(block, className)}
      {...a11yProps}
      {...props}
    />
  ) : null;
};
